import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Zap, Code, Smartphone, Palette, TrendingUp, ShoppingCart, Brain, BarChart, Cloud, Link as LinkIcon, PenTool } from 'lucide-react';
import ServiceContactForm from '../components/ServiceContactForm';

const Services = () => {
  const services = [
    {
      id: 1,
      title: 'Web Development',
      icon: Code,
      description: 'Custom web applications built with cutting-edge technologies for optimal performance and user experience.',
      features: ['React & Next.js', 'FastAPI & Node.js', 'Responsive Design', 'SEO Optimized'],
      color: '#d987ff',
      path: '/services/web-development'
    },
    {
      id: 2,
      title: 'Mobile App Development',
      icon: Smartphone,
      description: 'Native and cross-platform mobile applications that engage users and drive business growth.',
      features: ['iOS & Android', 'React Native', 'Flutter', 'App Store Launch'],
      color: '#ffd1e7',
      path: '/services/mobile-app'
    },
    {
      id: 3,
      title: 'UI/UX Design',
      icon: Palette,
      description: 'User-centric designs that combine aesthetics with functionality for exceptional experiences.',
      features: ['User Research', 'Wireframing', 'Prototyping', 'Design Systems'],
      color: '#88a2ff',
      path: '/services/ui-ux-design'
    },
    {
      id: 4,
      title: 'Digital Marketing',
      icon: TrendingUp,
      description: 'Data-driven marketing strategies that amplify your brand and drive measurable results.',
      features: ['SEO & SEM', 'Social Media', 'Content Marketing', 'Analytics'],
      color: '#b7fbff',
      path: '/services/digital-marketing'
    },
    {
      id: 5,
      title: 'E-Commerce Development',
      icon: ShoppingCart,
      description: 'Scalable e-commerce platforms that deliver seamless shopping experiences and boost sales.',
      features: ['Shopify & WooCommerce', 'Payment Integration', 'Inventory Management', 'Multi-channel'],
      color: '#ffd1e7',
      path: '/services/e-commerce'
    },
    {
      id: 6,
      title: 'AI & ML Solutions',
      icon: Brain,
      description: 'Intelligent solutions powered by artificial intelligence and machine learning technologies.',
      features: ['Predictive Analytics', 'NLP', 'Computer Vision', 'Automation'],
      color: '#d987ff',
      path: '/services/ai-ml'
    },
    {
      id: 7,
      title: 'Data Analytics',
      icon: BarChart,
      description: 'Transform raw data into actionable insights that drive informed business decisions.',
      features: ['Business Intelligence', 'Data Visualization', 'Reporting', 'Real-time Dashboards'],
      color: '#88a2ff',
      path: '/services/data-analytics'
    },
    {
      id: 8,
      title: 'Cloud Solutions',
      icon: Cloud,
      description: 'Scalable cloud infrastructure and migration services for modern businesses.',
      features: ['AWS & Azure', 'Cloud Migration', 'DevOps', 'Serverless'],
      color: '#b7fbff',
      path: '/services/cloud-solutions'
    },
    {
      id: 9,
      title: 'Blockchain Development',
      icon: LinkIcon,
      description: 'Secure blockchain solutions including smart contracts, DeFi, and Web3 applications.',
      features: ['Smart Contracts', 'NFT Platforms', 'DeFi', 'Web3 Integration'],
      color: '#d987ff',
      path: '/services/blockchain'
    },
    {
      id: 10,
      title: 'Creative Copywriting',
      icon: PenTool,
      description: 'Compelling content that tells your story, engages audiences, and drives conversions.',
      features: ['Brand Messaging', 'SEO Content', 'Email Campaigns', 'Social Copy'],
      color: '#ffd1e7',
      path: '/services/creative-copywriting'
    }
  ];

  const stats = [
    { value: '250+', label: 'Projects Delivered' },
    { value: '50+', label: 'Happy Clients' },
    { value: '10+', label: 'Services Offered' },
    { value: '98%', label: 'Client Satisfaction' }
  ];

  return (
    <div className="bg-white pt-16">
      {/* Hero Section */}
      <section className="relative py-32 px-8 lg:px-16 overflow-hidden bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 mb-6 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20"
            >
              <Sparkles size={20} className="text-light-pink" />
              <span className="text-sm font-mono uppercase tracking-wider text-white">Our Services</span>
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              Comprehensive Digital
              <br />
              <span className="bg-gradient-to-r from-light-pink to-light-blue bg-clip-text text-transparent">
                Solutions for Growth
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed mb-12">
              From concept to launch, we deliver end-to-end digital services that transform businesses and create lasting impact in the digital landscape.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-4xl md:text-5xl font-bold text-light-pink mb-2" style={{ fontWeight: 700 }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-white/70 font-semibold">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-8 lg:px-16">
        <div className="max-w-8xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ fontWeight: 700 }}>
              What We <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">Offer</span>
            </h2>
            <p className="text-xl text-dark-grey max-w-3xl mx-auto">
              Explore our comprehensive range of digital services designed to elevate your business
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <Link to={service.path}>
                  <div className="relative bg-white rounded-2xl p-8 border border-grey hover:border-transparent hover:shadow-2xl transition-all duration-300 h-full">
                    {/* Icon */}
                    <div 
                      className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${service.color}30` }}
                    >
                      <service.icon size={32} style={{ color: service.color }} />
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-light-pink transition-colors">
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="text-dark-grey mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-dark-grey">
                          <Check size={16} className="text-light-pink flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <div className="flex items-center gap-2 text-mid-purple font-semibold group-hover:gap-4 transition-all">
                      <span>Learn More</span>
                      <ArrowRight size={18} />
                    </div>

                    {/* Hover Gradient */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity -z-10"
                      style={{ background: `linear-gradient(135deg, ${service.color}, transparent)` }}
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-8 lg:px-16 bg-gradient-to-br from-grey via-white to-grey">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight" style={{ fontWeight: 700 }}>
                Why Choose
                <br />
                <span className="bg-gradient-to-r from-mid-purple to-light-pink bg-clip-text text-transparent">
                  Our Services
                </span>
              </h2>
              <p className="text-lg text-dark-grey leading-relaxed mb-8">
                We combine technical expertise with creative innovation to deliver solutions that don't just meet expectations—they exceed them.
              </p>

              <div className="space-y-6">
                {[
                  { title: 'Expert Team', description: 'Industry veterans with 15+ years of combined experience' },
                  { title: 'Proven Process', description: 'Agile methodology ensuring timely delivery and quality' },
                  { title: 'End-to-End Support', description: 'From strategy to maintenance, we\'re with you every step' },
                  { title: 'Results-Driven', description: 'Focused on measurable outcomes and ROI' }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl bg-light-pink flex items-center justify-center flex-shrink-0">
                      <Check size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-dark-grey">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative h-[500px] rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=1000&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-8 left-8 text-white">
                  <p className="text-2xl font-bold">Building The Future</p>
                  <p className="text-white/80">One Project at a Time</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 lg:px-8 bg-gradient-to-br from-[#1a0a2e] via-[#2d1b4e] to-[#1a0a2e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 2px, transparent 2px)',
              backgroundSize: '50px 50px'
            }}
          />
        </div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block mb-6"
            >
              <span className="text-8xl">🚀</span>
            </motion.div>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              Ready to Get Started?
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed max-w-3xl mx-auto">
              Let's discuss how our services can help you achieve your goals. Get in touch for a free consultation.
            </p>
            <motion.button
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-12 py-5 rounded-full bg-white text-black font-mono text-sm uppercase tracking-wider font-bold hover:bg-light-pink transition-all inline-flex items-center gap-3 group shadow-2xl"
            >
              Start Your Project
              <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <ServiceContactForm serviceName="General Inquiry" />
    </div>
  );
};

export default Services;
